import React, { useState, useEffect } from 'react';
import s from './AdminUsers.module.css';
import { api } from '../../../api';

interface User {
    id: number;
    name: string;
    email: string;
    dateCreating: string;
}

export const AdminUsers: React.FC = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
    });
    const [error, setError] = useState('');

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        try {
            setLoading(true);
            const response = await api.get('/admin/users');
            setUsers(response.data);
        } catch (error) {
            console.error('Ошибка загрузки пользователей:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (user?: User) => {
        if (user) {
            setEditingUser(user);
            setFormData({
                name: user.name,
                email: user.email,
                password: '',
            });
        } else {
            setEditingUser(null);
            setFormData({
                name: '',
                email: '',
                password: '',
            });
        }
        setError('');
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingUser(null);
        setFormData({
            name: '',
            email: '',
            password: '',
        });
    };

    const handleSubmit = async () => {
        try {
            if (!formData.name.trim() || !formData.email.trim()) {
                setError('Имя и email обязательны');
                return;
            }

            if (!editingUser && !formData.password.trim()) {
                setError('Пароль обязателен для нового пользователя');
                return;
            }

            if (editingUser) {
                await api.put(`/admin/users/${editingUser.id}`, formData);
            } else {
                await api.post('/admin/users', formData);
            }

            handleCloseModal();
            loadUsers();
        } catch (error: any) {
            setError(error.response?.data?.message || 'Ошибка сохранения');
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('Удалить пользователя?')) return;

        try {
            await api.delete(`/admin/users/${id}`);
            loadUsers();
        } catch (error: any) {
            alert(error.response?.data?.message || 'Ошибка удаления');
        }
    };

    if (loading) {
        return <div className={s.loading}>Загрузка...</div>;
    }

    return (
        <div className={s.usersPage}>
            <div className={s.header}>
                <h1 className={s.title}>Пользователи</h1>
                <button className={s.addBtn} onClick={() => handleOpenModal()}>
                    + Добавить пользователя
                </button>
            </div>

            <div className={s.tableWrapper}>
                <table className={s.table}>
                    <thead>
                    <tr>
                        <th>ID</th>
                        <th>Имя</th>
                        <th>Email</th>
                        <th>Дата создания</th>
                        <th>Действия</th>
                    </tr>
                    </thead>
                    <tbody>
                    {users.map(user => (
                        <tr key={user.id}>
                            <td>{user.id}</td>
                            <td>{user.name}</td>
                            <td>{user.email}</td>
                            <td>{new Date(user.dateCreating).toLocaleDateString()}</td>
                            <td>
                                <button
                                    className={s.editBtn}
                                    onClick={() => handleOpenModal(user)}
                                >
                                    ✏️
                                </button>
                                <button
                                    className={s.deleteBtn}
                                    onClick={() => handleDelete(user.id)}
                                    disabled={user.email === 'rdb-method@ya.ru'}
                                >
                                    🗑️
                                </button>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <div className={s.modalOverlay} onClick={handleCloseModal}>
                    <div className={s.modal} onClick={(e) => e.stopPropagation()}>
                        <h2>{editingUser ? 'Редактирование' : 'Новый пользователь'}</h2>

                        {error && <div className={s.error}>{error}</div>}

                        <div className={s.formGroup}>
                            <label>Имя *</label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Введите имя"
                            />
                        </div>

                        <div className={s.formGroup}>
                            <label>Email *</label>
                            <input
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                placeholder="user@example.com"
                            />
                        </div>

                        <div className={s.formGroup}>
                            <label>{editingUser ? 'Новый пароль (оставьте пустым, чтобы не менять)' : 'Пароль *'}</label>
                            <input
                                type="password"
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                placeholder="Введите пароль"
                            />
                        </div>

                        <div className={s.actions}>
                            <button className={s.cancelBtn} onClick={handleCloseModal}>Отмена</button>
                            <button className={s.saveBtn} onClick={handleSubmit}>Сохранить</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};